import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Row, Col, Form, Button, Card, Spinner, Modal, OverlayTrigger, Popover } from "react-bootstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { AGCSelect } from "../../generic-components/AGCSelect";
import { AGCDynamicSelect } from "../../generic-components/AGCDynamicSelect";
import { AGCText } from "../../generic-components/AGCText";
import { AGCTextArea } from "../../generic-components/AGCTextArea";
import { PurchaseItemsSection } from "./PurchaseItemsSection";
import { ENTITY_ID_SUPPLIER, ENTITY_ID_RAW_PURCHASE, ENTITY_ID_TAX_PLAN, ENTITY_ID_VEHICLE } from "../generic/Constants";
import { QrReviewExplanation } from "../qr-purchase/QrReviewExplanation";
import { apiGenericCreate, apiGenericUpdate, genericGetOne, genericGetAll, getNextPurchaseTicketNumber, getCurrentPurchaseTicketNumber, captureVehicleImage, captureAndSaveImage, getImagesForEntry, saveExistingImage, getAllOrgSettings } from "../generic/crud";
import { useVehicleCapture } from "../../hooks/useVehicleCapture";
import { computeRoundOffAmount } from "../../utils/CurrencyUtils";
import { WeighbridgeCaptureModal } from "../../components/weighbridge/WeighbridgeCaptureModal";
import { API_URL } from "../../config/api.config";
import { defaultRawPurchase } from "../generic/InitialStates";
import { showToast, TOAST_SUCCESS, TOAST_FAILURE } from "../../utils/toast/Toast";
import { useAuth } from "../../modules/auth";
import { PurchaseDumpLocationCards } from "./PurchaseDumpLocationCards";

// Validation schema
const PurchaseSchema = Yup.object().shape({
    purchaseTicketNumber: Yup.string().required("Ticket Number is required"),
    supplier: Yup.object().nullable().required("Supplier is required"),
    purchaseDate: Yup.date().required("Purchase date is required"),
    vehicle: Yup.object().nullable(),
    emptyWeight: Yup.number().nullable().min(0, "Must be >= 0"),
    loadedWeight: Yup.number().nullable().min(0, "Must be >= 0"),
    driverName: Yup.string(),
    battaAmount: Yup.number().min(0, "Must be >= 0"),
    inspectionNotes: Yup.string(),
    remarks: Yup.string(),
    roundOffAmount: Yup.number(),
    itemsList: Yup.array().min(1, "At least one item is required"),
});


export function PurchaseEntryForm({ id, readOnly = false }) {
    const navigate = useNavigate();
    const { currentUser } = useAuth();
    // Only show loading for edit mode (when we need to fetch data)
    const [isLoading, setIsLoading] = useState(!!id);
    const [isSaving, setIsSaving] = useState(false);
    const [taxPlansLookup, setTaxPlansLookup] = useState({});
    const [showValidationError, setShowValidationError] = useState(false);
    const [validationErrors, setValidationErrors] = useState([]);
    const [originalData, setOriginalData] = useState(null);
    // Vehicle capture states
    const { capture, isCapturing, weight: weightTrack, image: imageTrack, groupSelectModal } = useVehicleCapture(true);
    const [capturedLoadedImage, setCapturedLoadedImage] = useState(null);
    // Images loaded from database (for view mode)
    const [savedImages, setSavedImages] = useState([]);
    // Track whether ticket number is a preview (not yet committed)
    const [isTicketPreview, setIsTicketPreview] = useState(false);
    // Org settings for hardware control
    const [weighbridgeEnabled, setWeighbridgeEnabled] = useState(true);
    const [cameraEnabled, setCameraEnabled] = useState(true);
    // Controls whether price can be edited for purchase product items
    const [allowPriceEditPurchaseProduct, setAllowPriceEditPurchaseProduct] = useState(false);
    const [allowPurchaseCorrection, setAllowPurchaseCorrection] = useState(false);
    // Controls whether dump location cards are shown
    const [purchaseDumpLocationEnabled, setPurchaseDumpLocationEnabled] = useState(false);
    const [roundOffMode, setRoundOffMode] = useState('CEILING');
    // Step 2 mode: completing a 2-step (partial) purchase. Capture switches to EMPTY weight.
    const [isStep2Mode, setIsStep2Mode] = useState(false);

    // Form data state - following SaleEntryForm pattern
    const getDefaultFormData = useCallback(() => ({
        ...defaultRawPurchase,
        purchaseTicketNumber: "",
        purchaseDate: new Date(),
        purchasePersonId: currentUser?.id || "",
        vehicle: null,
        itemsList: [],
    }), [currentUser, id]);

    const [formData, setFormData] = useState(getDefaultFormData);

    // Formik setup - using formData state for initialValues
    const formik = useFormik({
        initialValues: formData,
        validationSchema: PurchaseSchema,
        onSubmit: async (values) => {
            setIsSaving(true);
            try {
                // Assign the real ticket number now (at save time) for:
                //   - brand-new purchases (!id), and
                //   - Step 2 completion of a partial 2-step record (originalData.isPartialRecord)
                // The partial Step-1 record has an empty ticket number; finalising it
                // should consume a real sequence number, just like a one-step purchase.
                let finalTicketNumber = values.purchaseTicketNumber || "";
                const needsTicket = !id || !!originalData?.isPartialRecord;
                if (needsTicket) {
                    // Never fall back to the preview number - it may already belong to another purchase.
                    try {
                        const ticketResponse = await getNextPurchaseTicketNumber();
                        finalTicketNumber = ticketResponse?.data?.formattedNumber;
                    } catch (error) {
                        console.error("Error fetching final purchase ticket number at save time:", error);
                        finalTicketNumber = "";
                    }
                    if (!finalTicketNumber) {
                        showToast("Could not get a ticket number. The purchase was not saved - please try again.", TOAST_FAILURE);
                        return;
                    }
                }

                // Determine partial-record fields for the payload.
                // - Step 2 completion: finalise (isPartialRecord=false), stamp purchaseSecondStepDate.
                // - Brand-new ONE_STEP: explicitly set isPartialRecord=false, purchaseType=ONE_STEP.
                const wasPartial = !!originalData?.isPartialRecord;
                const nowIso = new Date().toISOString();
                const partialFields = wasPartial
                    ? {
                        isPartialRecord: false,
                        purchaseType: originalData.purchaseType || "ONE_STEP",
                        purchaseFirstStepDate: originalData.purchaseFirstStepDate || null,
                        purchaseSecondStepDate: nowIso,
                    }
                    : (!id
                        ? { isPartialRecord: false, purchaseType: "ONE_STEP" }
                        : {});

                // On Step 2 completion, ensure purchaseDate reflects the final completion time
                // so reports/listings show the date the purchase was actually finalised.
                const effectivePurchaseDate = (wasPartial && (!values.purchaseDate || values.purchaseDate === null))
                    ? new Date(nowIso)
                    : (values.purchaseDate instanceof Date ? values.purchaseDate : new Date(values.purchaseDate));

                const purchaseData = {
                    purchaseTicketNumber: finalTicketNumber,
                    purchaseDate: effectivePurchaseDate,
                    // Extract vehicleNumber from vehicle object
                    vehicleNumber: values.vehicle?.name || values.vehicle?.vehicleNumber || values.vehicleNumber || "",
                    emptyWeight: values.emptyWeight,
                    loadedWeight: values.loadedWeight,
                    loadedWeightCorrection: parseFloat(values.loadedWeightCorrection) || 0,
                    driverName: values.driverName || "",
                    battaAmount: parseFloat(values.battaAmount) || 0,
                    inspectionNotes: values.inspectionNotes || "",
                    remarks: values.remarks || "",
                    roundOffAmount: parseFloat(values.roundOffAmount) || 0,
                    purchasePersonId: currentUser?.id || "",
                    dumpLocation: values.dumpLocation || "",
                    supplier: values.supplier ? {
                        id: values.supplier.id || values.supplier.value,
                        name: values.supplier.name || values.supplier.label
                    } : null,
                    itemsList: values.itemsList || [],
                    ...partialFields,
                };

                if (id && originalData) {
                    purchaseData.id = originalData.id || id;
                    purchaseData.orgId = originalData.orgId;
                    if (originalData.createdOn) purchaseData.createdOn = originalData.createdOn;
                    if (originalData.createdBy) purchaseData.createdBy = originalData.createdBy;
                    if (originalData.synced !== undefined) purchaseData.synced = originalData.synced;
                    // The update replaces the whole record, so carry over the QR Auto-Purchase fields.
                    // Completing a pending/draft record through this form resolves its review flags.
                    if (originalData.source) purchaseData.source = originalData.source;
                    if (originalData.weighbridgeId) purchaseData.weighbridgeId = originalData.weighbridgeId;
                    purchaseData.needsReview = wasPartial ? false : !!originalData.needsReview;
                    purchaseData.reviewReasons = wasPartial ? [] : (originalData.reviewReasons || []);
                    purchaseData.reviewNote = wasPartial ? null : (originalData.reviewNote || null);

                    await apiGenericUpdate(ENTITY_ID_RAW_PURCHASE, purchaseData, id);

                    // In Step 2 mode the captured image represents the EMPTY weight scan.
                    // Persist it now (the create path only handles new captures for LOADED_WEIGHT).
                    if (isStep2Mode && capturedLoadedImage && !capturedLoadedImage.saved && capturedLoadedImage.base64Image) {
                        const entryPrimaryId = originalData.primaryId || originalData.id || id;
                        if (entryPrimaryId) {
                            try {
                                await saveExistingImage(
                                    'PURCHASE',
                                    entryPrimaryId,
                                    'EMPTY_WEIGHT',
                                    capturedLoadedImage.base64Image,
                                    capturedLoadedImage.contentType
                                );
                            } catch (imgError) {
                                console.error("Error saving empty-weight image:", imgError);
                            }
                        }
                    }

                    // On Step 2 completion, update/create the Vehicle record with the
                    // newly-measured empty weight. Wrap in try/catch so failures here
                    // never block the main save (matches SaleEntryStep1Form pattern).
                    if (isStep2Mode && values.emptyWeight && parseFloat(values.emptyWeight) > 0) {
                        try {
                            await searchOrCreateVehicleByEmptyWeight(
                                purchaseData.vehicleNumber,
                                parseFloat(values.emptyWeight)
                            );
                        } catch (vehErr) {
                            console.error("Step 2 vehicle update failed:", vehErr);
                        }
                    }

                    showToast("Purchase updated successfully!", TOAST_SUCCESS);
                    navigate(`/purchase-print/${id}`);
                } else {
                    const response = await apiGenericCreate(ENTITY_ID_RAW_PURCHASE, purchaseData);
                    showToast("Purchase created successfully!", TOAST_SUCCESS);
                    const responseData = response?.data;
                    const newPurchaseId = responseData?.id || responseData?._id || responseData?.data?.id || responseData?.data?._id;
                    const newPurchasePrimaryId = responseData?.primaryId || responseData?.data?.primaryId || newPurchaseId;

                    // Save captured image if exists and not yet saved
                    if (capturedLoadedImage && !capturedLoadedImage.saved && newPurchasePrimaryId) {
                        try {
                            await saveExistingImage(
                                'PURCHASE',
                                newPurchasePrimaryId,
                                'LOADED_WEIGHT',
                                capturedLoadedImage.base64Image,
                                capturedLoadedImage.contentType
                            );
                        } catch (imgError) {
                            console.error("Error saving image:", imgError);
                            // Don't fail the whole operation if image save fails
                        }
                    }

                    if (newPurchaseId) {
                        navigate(`/purchase-print/${newPurchaseId}`);
                    } else {
                        navigate('/purchases-list');
                    }
                }
            } catch (error) {
                showToast("Error saving purchase: " + (error.response?.data || error.message), TOAST_FAILURE);
            } finally {
                setIsSaving(false);
            }
        },
        enableReinitialize: true,
    });

    // Track the previous vehicle ID to detect changes
    const previousVehicleIdRef = useRef(null);

    // Fetch vehicle's empty weight when a vehicle is selected or changed
    useEffect(() => {
        const vehicleId = formik.values.vehicle?.id || formik.values.vehicle?.value;
        const previousVehicleId = previousVehicleIdRef.current;

        const vehicleChanged = previousVehicleId !== null && vehicleId && vehicleId !== previousVehicleId;
        const isFirstSelection = previousVehicleId === null && vehicleId && !formik.values.emptyWeight;

        previousVehicleIdRef.current = vehicleId;

        if (vehicleChanged || isFirstSelection) {
            genericGetOne(ENTITY_ID_VEHICLE, vehicleId)
                .then(({ data }) => {
                    if (data && data.emptyWeight) {
                        const weight = parseFloat(data.emptyWeight);
                        if (!isNaN(weight) && weight > 0) {
                            formik.setFieldValue('emptyWeight', weight);
                        }
                    }
                })
                .catch((error) => {
                    console.error("Error fetching vehicle empty weight:", error);
                });
        }
    }, [formik.values.vehicle?.id, formik.values.vehicle?.value]);

    // Load org settings on mount
    useEffect(() => {
        getAllOrgSettings().then((res) => {
            const settings = res?.data || [];
            const getBool = (name, def) => { const s = settings.find(s => s.typeName === name); return s ? (s.value === true || s.value === 'true') : def; };
            setWeighbridgeEnabled(getBool('ENABLE_WEIGHBRIDGE_CAPTURE', true));
            setCameraEnabled(getBool('ENABLE_CAMERA_CAPTURE', true));
            setAllowPriceEditPurchaseProduct(getBool('ALLOW_PRICE_EDIT_PURCHASE_PRODUCT_ITEMS', false));
            setAllowPurchaseCorrection(getBool('ALLOW_PURCHASE_CORRECTION', false));
            setPurchaseDumpLocationEnabled(getBool('PURCHASE_DROP_LOCATION', false));
            const roundOffSetting = settings.find(s => s.typeName === 'ROUND_OFF_MODE');
            setRoundOffMode(roundOffSetting?.value ? String(roundOffSetting.value) : 'CEILING');
        }).catch(() => {});
    }, []);

    // Load tax plans for calculations
    useEffect(() => {
        genericGetAll(ENTITY_ID_TAX_PLAN, { pageNumber: 1, pageSize: 1000 })
            .then((response) => {
                const lookup = {};
                const responseData = response?.data || response;
                const taxPlans = responseData?.entities || responseData?.data || [];

                if (Array.isArray(taxPlans)) {
                    taxPlans.forEach(taxPlan => {
                        if (taxPlan.id) lookup[taxPlan.id] = taxPlan;
                        if (taxPlan.name) lookup[taxPlan.name] = taxPlan;
                    });
                }
                setTaxPlansLookup(lookup);
            })
            .catch((error) => {
                console.error("Error loading tax plans:", error);
            });
    }, []);

    // Load existing purchase data for edit/view mode
    useEffect(() => {
        if (id) {
            setIsLoading(true);
            genericGetOne(ENTITY_ID_RAW_PURCHASE, id)
                .then(async ({ data }) => {
                    setOriginalData(data);
                    // Detect Step 2 completion mode for 2-step purchases
                    // A QR auto-purchase draft missing its empty weight is completed the same way
                    const isQrDraftAwaitingEmpty = data?.source === "QR_AUTO" && data?.loadedWeight != null && data?.emptyWeight == null;
                    // A weightless QR draft (scale/weighbridge failed: loadedWeight == null) has nothing to
                    // complete in step 2, so it opens in the normal full form (capture loaded, empty from vehicle).
                    const isStep2 = data?.isPartialRecord === true && data?.loadedWeight != null
                        && (data?.purchaseType === "TWO_STEP" || isQrDraftAwaitingEmpty);
                    if (isStep2) {
                        setIsStep2Mode(true);
                    }

                    // For Step 2 completion, the partial Step-1 record has an empty
                    // purchaseTicketNumber. Yup requires it to be non-empty, so fetch
                    // a preview number now — the real one is assigned at save time.
                    // Any other pending record being completed here (e.g. a QR draft) needs the same preview.
                    let resolvedTicketNumber = data.purchaseTicketNumber || "";
                    if (data?.isPartialRecord === true && !resolvedTicketNumber) {
                        try {
                            const ticketResponse = await getCurrentPurchaseTicketNumber();
                            const currentNumber = ticketResponse?.data?.currentNumber || 0;
                            resolvedTicketNumber = String(currentNumber + 1).padStart(4, '0');
                            setIsTicketPreview(true);
                        } catch (error) {
                            console.error("Error fetching preview ticket number for Step 2:", error);
                            const now = new Date();
                            resolvedTicketNumber = `PT-${now.getFullYear().toString().slice(-2)}${(now.getMonth() + 1).toString().padStart(2, '0')}${now.getDate().toString().padStart(2, '0')}-${now.getHours().toString().padStart(2, '0')}${now.getMinutes().toString().padStart(2, '0')}${now.getSeconds().toString().padStart(2, '0')}`;
                        }
                    }

                    // Use setFormData to update form - formik will reinitialize with enableReinitialize: true
                    setFormData({
                        ...defaultRawPurchase,
                        ...data,
                        purchaseTicketNumber: resolvedTicketNumber,
                        purchaseDate: data.purchaseDate ? new Date(data.purchaseDate) : new Date(),
                        // Transform vehicleNumber string to vehicle object for display
                        vehicle: data.vehicleNumber ? {
                            name: data.vehicleNumber,
                            label: data.vehicleNumber,
                            vehicleNumber: data.vehicleNumber
                        } : null,
                        // Ensure supplier is properly formatted
                        supplier: data.supplier ? {
                            id: data.supplier.id || data.supplier._id,
                            name: data.supplier.name,
                            value: data.supplier.id || data.supplier._id,
                            label: data.supplier.name
                        } : null,
                    });

                    // Pending record opened in the full form without an empty weight (vehicle is shown
                    // read-only, so the vehicle lookup above never fires): prefill it from the Vehicle master.
                    if (data?.isPartialRecord === true && !isStep2 && data.emptyWeight == null && data.vehicleNumber) {
                        genericGetAll(ENTITY_ID_VEHICLE, { fixedFilterColumn: 'vehicleNumber', fixedFilterData: data.vehicleNumber, sizePerPage: 1 })
                            .then((res) => {
                                const vehicle = (res?.data?.entities || [])[0];
                                const weight = vehicle ? parseFloat(vehicle.emptyWeight) : NaN;
                                if (!isNaN(weight) && weight > 0) {
                                    setFormData(prev => ({ ...prev, emptyWeight: weight }));
                                }
                            })
                            .catch(err => console.error("Error loading vehicle empty weight:", err));
                    }

                    // Load captured images for this purchase
                    const purchasePrimaryId = data.primaryId || data.id || id;
                    if (purchasePrimaryId) {
                        getImagesForEntry('PURCHASE', purchasePrimaryId)
                            .then(imgResponse => {
                                const imgData = imgResponse?.data;
                                if (imgData?.success && imgData?.images) {
                                    setSavedImages(imgData.images);
                                }
                            })
                            .catch(err => console.error("Error loading images:", err));
                    }

                    setIsLoading(false);
                })
                .catch((error) => {
                    showToast("Error loading purchase: " + error.message, TOAST_FAILURE);
                    setIsLoading(false);
                    navigate('/purchases-list');
                });
        } else {
            // For new purchases, show preview ticket number (no increment)
            const initializeNewPurchase = async () => {
                try {
                    const ticketResponse = await getCurrentPurchaseTicketNumber();
                    const currentNumber = ticketResponse?.data?.currentNumber || 0;
                    const previewNumber = String(currentNumber + 1).padStart(4, '0');
                    setFormData(prev => ({
                        ...prev,
                        purchaseTicketNumber: previewNumber,
                    }));
                    setIsTicketPreview(true);
                } catch (error) {
                    console.error("Error fetching preview purchase ticket number:", error);
                    // Fallback to timestamp-based if API fails
                    const now = new Date();
                    const fallbackNumber = `PT-${now.getFullYear().toString().slice(-2)}${(now.getMonth() + 1).toString().padStart(2, '0')}${now.getDate().toString().padStart(2, '0')}-${now.getHours().toString().padStart(2, '0')}${now.getMinutes().toString().padStart(2, '0')}${now.getSeconds().toString().padStart(2, '0')}`;
                    setFormData(prev => ({
                        ...prev,
                        purchaseTicketNumber: fallbackNumber,
                    }));
                }
            };
            initializeNewPurchase();
        }
    }, [id, navigate]);

    // Parse number safely
    const parseNumber = useCallback((value) => {
        if (value === null || value === undefined || value === '') return 0;
        const parsed = parseFloat(value);
        return isNaN(parsed) ? 0 : parsed;
    }, []);

    // Handle items change - matching SaleEntryForm pattern
    const handleItemsChange = useCallback((items) => {
        formik.setFieldValue('itemsList', items);
    }, [formik]);

    // Handle cancel
    const handleCancel = () => {
        // A QR draft being edited goes back to Draft Purchases; everything else to the purchases list
        const isQrDraft = originalData?.source === "QR_AUTO" && originalData?.isPartialRecord === true;
        navigate(isQrDraft ? '/draft-purchases' : '/purchases-list');
    };

    // Search-or-create vehicle when Step 2 finishes. Mirrors SaleEntryStep1Form's
    // searchOrCreateVehicle so the Vehicle record stores the latest known empty
    // weight regardless of which flow first measured it.
    const searchOrCreateVehicleByEmptyWeight = async (vehicleNumber, emptyWeight) => {
        if (!vehicleNumber) return;
        const searchParams = {
            fixedFilterColumn: 'vehicleNumber',
            fixedFilterData: vehicleNumber,
            sizePerPage: 1,
        };
        const response = await genericGetAll(ENTITY_ID_VEHICLE, searchParams);
        const vehicles = response?.data?.entities || response?.data?.data || [];

        if (vehicles.length > 0) {
            const existingVehicle = vehicles[0];
            await apiGenericUpdate(ENTITY_ID_VEHICLE, {
                ...existingVehicle,
                emptyWeight: emptyWeight,
            }, existingVehicle.id);
        } else {
            await apiGenericCreate(ENTITY_ID_VEHICLE, {
                vehicleNumber: vehicleNumber,
                emptyWeight: emptyWeight,
                modelMake: '',
            });
        }
    };

    // Handle vehicle data capture (weight and image)
    // Respects weighbridgeEnabled and cameraEnabled org settings.
    // In Step 2 mode the capture targets EMPTY weight (truck is now empty after
    // unloading); otherwise it targets LOADED weight as for normal 1-step purchases.
    const handleCapture = async () => {
        const entryPrimaryId = originalData?.primaryId || id;
        const isEditMode = !!entryPrimaryId;
        const weightField = isStep2Mode ? 'emptyWeight' : 'loadedWeight';
        const imageType = isStep2Mode ? 'EMPTY_WEIGHT' : 'LOADED_WEIGHT';
        const wantWeight = isWeightBasedItem && weighbridgeEnabled;
        const wantImage = cameraEnabled;
        if (!wantWeight && !wantImage) return;

        // In edit mode (Step 1), capture AND save the image immediately. Step 2's
        // empty-weight image is saved after the partial record is finalised, so it
        // stays an in-memory grab.
        const saveNow = isEditMode && !isStep2Mode;
        const imageFn = saveNow
            ? (groupId) => captureAndSaveImage('PURCHASE', entryPrimaryId, imageType, groupId)
            : captureVehicleImage;

        const res = await capture({ weight: wantWeight, image: wantImage, imageFn });
        if (res.cancelled) return;

        if (res.weight != null) {
            formik.setFieldValue(weightField, res.weight);
            showToast("Weight captured: " + res.weight, TOAST_SUCCESS);
        } else if (wantWeight) {
            showToast(res.weightError?.message || "Failed to capture weight", TOAST_FAILURE);
        }

        if (res.image) {
            const d = res.image;
            if (d.primaryId) {
                setCapturedLoadedImage({
                    url: d.localUrl || `data:${d.contentType};base64,${d.imageData}`,
                    primaryId: d.primaryId,
                    saved: true
                });
                if (!wantWeight) showToast("Image captured and saved", TOAST_SUCCESS);
            } else {
                setCapturedLoadedImage({
                    url: `data:${d.contentType};base64,${d.imageData}`,
                    base64Image: d.imageData,
                    contentType: d.contentType,
                    saved: false
                });
                if (!wantWeight) showToast("Image captured (will be saved with purchase)", TOAST_SUCCESS);
            }
        } else if (wantImage) {
            showToast(res.imageError?.message || "Failed to capture image", TOAST_FAILURE);
        }
    };

    // Handle form submission with validation
    const handleSubmitWithValidation = useCallback((values) => {
        const errors = [];

        if (!values.supplier) {
            errors.push("Please select a Supplier");
        }

        // Determine if item is weight-based for validation
        const inventoryItems = (values.itemsList || []).filter(item => item.itemType === 'INVENTORY');
        const invItem = inventoryItems.length > 0 ? inventoryItems[0] : null;
        const isWeightBased = !invItem || !invItem.unitSymbol || invItem.unitSymbol.toLowerCase() === 'ton';

        if (isWeightBased) {
            if (!values.loadedWeight || parseFloat(values.loadedWeight) <= 0) {
                errors.push("Please enter the Loaded Weight");
            }

            if (!values.emptyWeight || parseFloat(values.emptyWeight) <= 0) {
                errors.push("Please enter the Empty Weight");
            }

        } else {
            // For non-weight items, validate that quantity is entered
            if (invItem && (!invItem.quantity || parseFloat(invItem.quantity) <= 0)) {
                errors.push("Please enter a valid Quantity for the inventory item");
            }
        }

        // Whenever both weights are entered (any unit, 1-step or 2-step), empty must be below loaded
        if (values.loadedWeight && values.emptyWeight && parseFloat(values.loadedWeight) <= parseFloat(values.emptyWeight)) {
            errors.push("Loaded Weight must be greater than Empty Weight");
        }

        if (inventoryItems.length === 0) {
            errors.push("Please add at least one Inventory Item");
        }

        if (purchaseDumpLocationEnabled && !values.dumpLocation) {
            errors.push("Please select a Dump Location");
        }

        if (cameraEnabled && !capturedLoadedImage && savedImages.length === 0) {
            errors.push("Please capture a vehicle image before saving");
        }

        if (errors.length > 0) {
            setValidationErrors(errors);
            setShowValidationError(true);
            return;
        }

        formik.submitForm();
    }, [formik, purchaseDumpLocationEnabled, cameraEnabled, capturedLoadedImage, savedImages]);

    // Keyboard shortcuts
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.ctrlKey && e.key === 's' && !readOnly) {
                e.preventDefault();
                handleSubmitWithValidation(formik.values);
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [formik, handleSubmitWithValidation, readOnly]);

    if (isLoading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
                <Spinner animation="border" variant="primary" />
                <span className="ms-2">Loading purchase data...</span>
            </div>
        );
    }

    const correctionAmount = parseNumber(formik.values.loadedWeightCorrection);
    const effectiveLoaded = parseNumber(formik.values.loadedWeight) - correctionAmount;
    const netWeight = effectiveLoaded - parseNumber(formik.values.emptyWeight);

    // Determine if the selected inventory item is weight-based (Ton) or non-weight (e.g., Cft)
    const isWeightBasedItem = (() => {
        const invItem = (formik.values.itemsList || []).find(item => item.itemType === 'INVENTORY');
        if (!invItem || !invItem.unitSymbol) return true; // Default to weight-based
        return invItem.unitSymbol.toLowerCase() === 'ton';
    })();

    return (
        <>
        <Form onSubmit={formik.handleSubmit}>
            <div className="container-fluid px-0">
                {!readOnly && originalData?.needsReview === true && (
                    <div className="alert alert-warning d-flex align-items-start mb-3" role="alert">
                        <i className="bi bi-exclamation-triangle-fill me-3 fs-4 text-warning"></i>
                        <div>
                            <strong>Why this purchase is a draft</strong>
                            <div className="mt-2">
                                <QrReviewExplanation reasons={originalData.reviewReasons} note={originalData.reviewNote} />
                            </div>
                        </div>
                    </div>
                )}
                {isStep2Mode && (
                    <div className="alert alert-warning d-flex align-items-center mb-3" role="alert">
                        <i className="bi bi-exclamation-triangle-fill me-3 fs-4 text-warning"></i>
                        <div>
                            <strong>Step 2 — Completing 2-step purchase.</strong>
                            <br />
                            <small>Capture empty weight and finalise items.</small>
                        </div>
                    </div>
                )}
                <Row>
                    {/* LEFT COLUMN - Primary Purchase Data */}
                    <Col lg={8}>
                        {/* Combined Purchase Info Card */}
                        <Card className="mb-3 border-0 shadow-sm" style={{ borderRadius: '8px' }}>
                            {readOnly && (
                                <Card.Header className="py-2 px-4" style={{ borderRadius: '8px 8px 0 0', backgroundColor: '#e8f4fc' }}>
                                    <div className="d-flex align-items-center">
                                        <i className="bi bi-receipt me-2" style={{ color: '#4a90a4' }}></i>
                                        <span className="fw-semibold small" style={{ color: '#4a90a4' }}>PURCHASE INFORMATION</span>
                                    </div>
                                </Card.Header>
                            )}
                            <Card.Body className="p-4">
                                <Row className="mb-3">
                                    <Col md={4}>
                                        <div className="mb-3">
                                            <label className="form-label text-muted small">Ticket No.</label>
                                            <div className="fw-semibold fs-5 d-flex align-items-center gap-2">
                                                {formik.values.purchaseTicketNumber || (readOnly ? 'N/A' : 'Loading...')}
                                                {!readOnly && isTicketPreview && (
                                                    <OverlayTrigger
                                                        trigger={['hover', 'focus', 'click']}
                                                        placement="right"
                                                        rootClose
                                                        overlay={
                                                            <Popover style={{ maxWidth: '300px' }}>
                                                                <Popover.Header as="div" className="d-flex align-items-center gap-2" style={{ background: '#f0f4ff', borderBottom: '2px solid #4a90a4' }}>
                                                                    <i className="bi bi-info-circle-fill" style={{ color: '#4a90a4' }}></i>
                                                                    <strong style={{ color: '#2c3e50' }}>Preview Ticket Number</strong>
                                                                </Popover.Header>
                                                                <Popover.Body style={{ fontSize: '13px', lineHeight: '1.5' }}>
                                                                    <p className="mb-2">This is a <strong>preview</strong> of the next ticket number. The final number will be assigned when you <strong>save</strong> the purchase.</p>
                                                                    <p className="mb-0 text-muted" style={{ fontSize: '12px' }}>
                                                                        <i className="bi bi-people-fill me-1"></i>
                                                                        If multiple purchases are being created at the same time, the final number may differ slightly.
                                                                    </p>
                                                                </Popover.Body>
                                                            </Popover>
                                                        }
                                                    >
                                                        <span
                                                            tabIndex={0}
                                                            style={{ cursor: 'pointer', color: '#4a90a4', fontSize: '14px' }}
                                                        >
                                                            <i className="bi bi-info-circle-fill"></i>
                                                        </span>
                                                    </OverlayTrigger>
                                                )}
                                            </div>
                                            {!readOnly && (
                                                <small className="text-muted">{isTicketPreview ? 'Preview — assigned on save' : 'Auto-generated'}</small>
                                            )}
                                        </div>
                                    </Col>
                                    <Col md={4}>
                                        {readOnly ? (
                                            <div className="mb-3">
                                                <label className="form-label text-muted small">Supplier</label>
                                                <div className="fw-semibold">{formik.values.supplier?.label || formik.values.supplier?.name || '-'}</div>
                                            </div>
                                        ) : (
                                            <Form.Group>
                                                <AGCDynamicSelect
                                                    entityId={ENTITY_ID_SUPPLIER}
                                                    formik={formik}
                                                    name="Supplier"
                                                    id="supplier"
                                                    value={formik.values.supplier}
                                                    toolTip="Select supplier"
                                                    required={true}
                                                    searchField="name"
                                                />
                                            </Form.Group>
                                        )}
                                    </Col>
                                    <Col md={4}>
                                        {readOnly || (id && originalData?.isPartialRecord) ? (
                                            <div className="mb-3">
                                                <label className="form-label text-muted small">Vehicle</label>
                                                <div className="fw-semibold">{formik.values.vehicle?.label || formik.values.vehicle?.vehicleNumber || '-'}</div>
                                            </div>
                                        ) : (
                                            <Form.Group>
                                                <AGCDynamicSelect
                                                    entityId={ENTITY_ID_VEHICLE}
                                                    formik={formik}
                                                    name="Vehicle"
                                                    id="vehicle"
                                                    value={formik.values.vehicle}
                                                    toolTip="Select vehicle"
                                                    required={false}
                                                    searchField="vehicleNumber"
                                                />
                                            </Form.Group>
                                        )}
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>

                        {/* Items Section */}
                        <Card className="mb-3 border-0 shadow-sm" style={{ borderRadius: '8px' }}>
                            <Card.Body className="p-0">
                                <PurchaseItemsSection
                                    items={formik.values.itemsList || []}
                                    onChange={handleItemsChange}
                                    defaultQuantity={isWeightBasedItem && netWeight > 0 ? parseFloat(netWeight.toFixed(3)) : 1}
                                    taxPlansLookup={taxPlansLookup}
                                    showInventory={true}
                                    showServices={true}
                                    readOnly={readOnly}
                                    supplierId={formik.values.supplier?.id || formik.values.supplier?.value || null}
                                    isWeightBased={isWeightBasedItem}
                                    allowPriceEditProduct={allowPriceEditPurchaseProduct}
                                />
                            </Card.Body>
                        </Card>

                        {/* Bill Summary */}
                        <Card className="mb-3 border-0 shadow-sm" style={{ borderRadius: '8px' }}>
                            <Card.Header className="py-2 px-4" style={{ borderRadius: '8px 8px 0 0', backgroundColor: '#e8f4fc', minHeight: '42px' }}>
                                <div className="d-flex align-items-center">
                                    <i className="bi bi-calculator me-2" style={{ color: '#4a90a4' }}></i>
                                    <span className="fw-semibold small" style={{ color: '#4a90a4' }}>BILL SUMMARY</span>
                                </div>
                            </Card.Header>
                            <Card.Body className="p-3">
                                <BillCalculations
                                    items={formik.values.itemsList || []}
                                    formik={formik}
                                    taxPlansLookup={taxPlansLookup}
                                    readOnly={readOnly}
                                    roundOffMode={roundOffMode}
                                />
                            </Card.Body>
                        </Card>

                        {/* Dump Location Cards - only when setting enabled */}
                        {purchaseDumpLocationEnabled && (
                            <PurchaseDumpLocationCards
                                value={formik.values.dumpLocation || ""}
                                onChange={(val) => formik.setFieldValue('dumpLocation', val)}
                                readOnly={readOnly}
                            />
                        )}
                    </Col>

                    {/* RIGHT COLUMN - Additional Info & Actions */}
                    <Col lg={4}>
                        {/* Weight / Image Panel OR Quantity Panel */}
                        {isWeightBasedItem ? (
                            <Card className="mb-3 border-0 shadow-sm" style={{ borderRadius: '8px' }}>
                                <Card.Header className="d-flex align-items-center justify-content-between py-2 px-4" style={{ borderRadius: '8px 8px 0 0', backgroundColor: '#e8f4fc', minHeight: '42px' }}>
                                    <div className="d-flex align-items-center">
                                        <i className={`bi ${readOnly ? 'bi-speedometer' : weighbridgeEnabled ? 'bi-camera' : 'bi-pencil-square'} me-2`} style={{ color: '#4a90a4' }}></i>
                                        <span className="fw-semibold small" style={{ color: '#4a90a4' }}>
                                            {readOnly ? 'WEIGHT INFORMATION' : weighbridgeEnabled ? 'VEHICLE IMAGES' : 'WEIGHT & IMAGES'}
                                        </span>
                                    </div>
                                    {!readOnly && (
                                        <span className="badge text-white" style={{ fontSize: '0.65rem', backgroundColor: weighbridgeEnabled ? '#4a90a4' : '#f59e0b' }}>
                                            {weighbridgeEnabled ? 'AUTO CAPTURE' : 'MANUAL ENTRY'}
                                        </span>
                                    )}
                                </Card.Header>
                                <Card.Body className="p-3">
                                    {!readOnly && (
                                        <>
                                            {/* Image Section */}
                                            {cameraEnabled ? (
                                                <div className="mb-3">
                                                    {/* Step 1 saved LOADED image — shown in Step 2 so operator can see what arrived */}
                                                    {isStep2Mode && (() => {
                                                        const stepOneImage = savedImages.find(img => img.imageType === 'LOADED_WEIGHT');
                                                        if (!stepOneImage) return null;
                                                        return (
                                                            <div className="mb-2 border rounded overflow-hidden" style={{ borderColor: '#dee2e6' }}>
                                                                <div className="bg-light px-2 py-1 d-flex justify-content-between align-items-center" style={{ fontSize: '0.7rem' }}>
                                                                    <span className="text-muted"><i className="bi bi-image me-1"></i>Step 1 — Loaded</span>
                                                                    <span className="badge bg-info bg-opacity-10 text-info" style={{ fontSize: '0.6rem' }}>LOADED</span>
                                                                </div>
                                                                <img
                                                                    src={`${API_URL}/api/vehicle-capture/image/${stepOneImage.primaryId}`}
                                                                    alt="Step 1 loaded vehicle"
                                                                    style={{ width: '100%', height: 'auto', display: 'block' }}
                                                                />
                                                            </div>
                                                        );
                                                    })()}
                                                    {capturedLoadedImage ? (
                                                        <div className="border rounded overflow-hidden" style={{ borderColor: '#198754' }}>
                                                            {isStep2Mode && (
                                                                <div className="bg-light px-2 py-1 d-flex justify-content-between align-items-center" style={{ fontSize: '0.7rem' }}>
                                                                    <span className="text-muted"><i className="bi bi-image me-1"></i>Step 2 — Empty</span>
                                                                    <span className="badge bg-success bg-opacity-10 text-success" style={{ fontSize: '0.6rem' }}>EMPTY</span>
                                                                </div>
                                                            )}
                                                            <img
                                                                src={capturedLoadedImage.url}
                                                                alt="Captured vehicle"
                                                                style={{ width: '100%', height: 'auto', display: 'block' }}
                                                            />
                                                        </div>
                                                    ) : (
                                                        <div
                                                            className="border rounded d-flex flex-column align-items-center justify-content-center bg-light"
                                                            style={{ height: '120px', borderStyle: 'dashed', borderColor: '#adb5bd' }}
                                                        >
                                                            <i className="bi bi-camera text-muted" style={{ fontSize: '2rem', opacity: 0.4 }}></i>
                                                            <span className="badge bg-light text-muted mt-1" style={{ fontSize: '0.7rem' }}>
                                                                {isStep2Mode ? 'Awaiting empty-weight capture' : 'Awaiting capture'}
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                <div className="text-center p-3 mb-3 rounded" style={{ backgroundColor: '#fff3cd', border: '1px dashed #ffc107' }}>
                                                    <i className="bi bi-camera-video-off text-warning" style={{ fontSize: '1.5rem' }}></i>
                                                    <div className="text-muted small mt-1">Camera capture is disabled</div>
                                                    <div className="text-muted" style={{ fontSize: '0.7rem' }}>Vehicle images are not required for this purchase</div>
                                                </div>
                                            )}

                                            {/* Weight section - Manual inputs when weighbridge disabled */}
                                            {!weighbridgeEnabled ? (
                                                <Row className="g-2">
                                                    <Col xs={6}>
                                                        <Form.Group>
                                                            <Form.Label className="small text-muted mb-1">Empty Weight (Ton)</Form.Label>
                                                            <Form.Control
                                                                size="sm"
                                                                type="number"
                                                                step="0.001"
                                                                min="0"
                                                                placeholder="0.000"
                                                                value={formik.values.emptyWeight || ''}
                                                                onChange={(e) => formik.setFieldValue('emptyWeight', e.target.value ? parseFloat(e.target.value) : null)}
                                                            />
                                                        </Form.Group>
                                                    </Col>
                                                    <Col xs={6}>
                                                        <Form.Group>
                                                            <Form.Label className="small text-muted mb-1">Loaded Weight (Ton)</Form.Label>
                                                            <Form.Control
                                                                size="sm"
                                                                type="number"
                                                                step="0.001"
                                                                min="0"
                                                                placeholder="0.000"
                                                                value={formik.values.loadedWeight || ''}
                                                                onChange={(e) => formik.setFieldValue('loadedWeight', e.target.value ? parseFloat(e.target.value) : null)}
                                                            />
                                                        </Form.Group>
                                                    </Col>
                                                </Row>
                                            ) : (
                                                <Row className="g-2">
                                                    <Col xs={6}>
                                                        <div className="text-center p-2 rounded" style={{ backgroundColor: '#f8f9fa' }}>
                                                            <small className="text-muted d-block" style={{ fontSize: '0.7rem' }}>Empty Weight</small>
                                                            <span className="fw-bold" style={{ fontSize: '1.1rem', color: formik.values.emptyWeight ? '#0d6efd' : '#6c757d' }}>
                                                                {formik.values.emptyWeight ? `${formik.values.emptyWeight} Ton` : '-- Ton'}
                                                            </span>
                                                        </div>
                                                    </Col>
                                                    <Col xs={6}>
                                                        <div className="text-center p-2 rounded" style={{ backgroundColor: '#f8f9fa' }}>
                                                            <small className="text-muted d-block" style={{ fontSize: '0.7rem' }}>Loaded Weight</small>
                                                            <span className="fw-bold" style={{ fontSize: '1.1rem', color: formik.values.loadedWeight ? '#198754' : '#6c757d' }}>
                                                                {formik.values.loadedWeight ? `${formik.values.loadedWeight} Ton` : '-- Ton'}
                                                            </span>
                                                        </div>
                                                    </Col>
                                                </Row>
                                            )}
                                        </>
                                    )}

                                    {readOnly && (
                                        <Row className="g-2">
                                            <Col xs={6}>
                                                <div className="text-center">
                                                    <small className="text-muted d-block">Empty Weight</small>
                                                    <span className="fw-bold" style={{ fontSize: '1.1rem', color: formik.values.emptyWeight ? '#0d6efd' : '#6c757d' }}>
                                                        {formik.values.emptyWeight ? `${formik.values.emptyWeight} Ton` : '-- Ton'}
                                                    </span>
                                                </div>
                                            </Col>
                                            <Col xs={6}>
                                                <div className="text-center">
                                                    <small className="text-muted d-block">Loaded Weight</small>
                                                    <span className="fw-bold" style={{ fontSize: '1.1rem', color: formik.values.loadedWeight ? '#0d6efd' : '#6c757d' }}>
                                                        {formik.values.loadedWeight ? `${formik.values.loadedWeight} Ton` : '-- Ton'}
                                                    </span>
                                                </div>
                                            </Col>
                                        </Row>
                                    )}

                                    {/* Correction (wastage) — editable in form mode, read-only in view mode.
                                        Gated by ALLOW_PURCHASE_CORRECTION; in read-only view we still surface
                                        existing non-zero corrections from historical purchases so the data
                                        doesn't disappear if the setting is later turned off. */}
                                    {parseNumber(formik.values.loadedWeight) > 0 && (allowPurchaseCorrection || (readOnly && correctionAmount > 0)) && (
                                        <div className="mt-2 p-2 rounded" style={{ backgroundColor: '#fff', border: '1px solid #dee2e6' }}>
                                            <div className="d-flex align-items-center justify-content-between">
                                                <small className="text-muted">
                                                    <i className="bi bi-dash-circle me-1"></i>Correction (wastage)
                                                </small>
                                                {readOnly ? (
                                                    <span className="fw-semibold" style={{ fontSize: '0.95rem', color: correctionAmount > 0 ? '#dc3545' : '#6c757d' }}>
                                                        {correctionAmount > 0 ? `${correctionAmount.toFixed(3)} Ton` : '0 Ton'}
                                                    </span>
                                                ) : (
                                                    <div className="d-flex align-items-center gap-2">
                                                        <Form.Control
                                                            type="number"
                                                            step="0.001"
                                                            min="0"
                                                            placeholder="0"
                                                            value={formik.values.loadedWeightCorrection || ''}
                                                            onChange={(e) => {
                                                                const parsed = e.target.value ? parseFloat(e.target.value) : 0;
                                                                formik.setFieldValue('loadedWeightCorrection', Number.isFinite(parsed) && parsed > 0 ? parsed : 0);
                                                            }}
                                                            onKeyDown={(e) => { if (e.key === '-' || e.key === 'e' || e.key === 'E') e.preventDefault(); }}
                                                            style={{ width: '90px', textAlign: 'right' }}
                                                            size="sm"
                                                        />
                                                        <small className="text-muted">Ton</small>
                                                    </div>
                                                )}
                                            </div>
                                            {correctionAmount > 0 && (
                                                <div className="text-end mt-1">
                                                    <small className="text-success fw-semibold">
                                                        Effective Loaded: {effectiveLoaded.toFixed(3)} Ton
                                                    </small>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Net Weight Display */}
                                    <div className="mt-3 p-2 rounded text-center" style={{ backgroundColor: netWeight > 0 ? '#d1e7dd' : '#f8f9fa', border: netWeight > 0 ? '1px solid #badbcc' : '1px solid #dee2e6' }}>
                                        <small className="text-muted d-block" style={{ fontSize: '0.7rem' }}>Net Weight</small>
                                        <span className="fw-bold" style={{ fontSize: '1.4rem', color: netWeight > 0 ? '#198754' : '#6c757d' }}>
                                            {netWeight > 0 ? `${netWeight.toFixed(3)} Ton` : '-- Ton'}
                                        </span>
                                    </div>

                                    {!readOnly && (weighbridgeEnabled || cameraEnabled) && (
                                        <>
                                            <div className="d-grid mt-3">
                                                <Button
                                                    variant="light"
                                                    size="sm"
                                                    className="text-primary"
                                                    style={{
                                                        border: '1px solid #86b7fe',
                                                        backgroundColor: '#f8f9fa'
                                                    }}
                                                    onClick={handleCapture}
                                                    disabled={isCapturing}
                                                >
                                                    {isCapturing ? (
                                                        <>
                                                            <Spinner animation="border" size="sm" className="me-2" />
                                                            Capturing...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <i className="bi bi-camera me-2"></i>
                                                            {weighbridgeEnabled && cameraEnabled ? 'Capture' : cameraEnabled ? 'Capture Image' : 'Capture Weight'}
                                                        </>
                                                    )}
                                                </Button>
                                            </div>
                                            <div className="text-center mt-2">
                                                <small className="text-muted" style={{ fontSize: '0.7rem' }}>
                                                    <i className="bi bi-info-circle me-1"></i>
                                                    {weighbridgeEnabled && cameraEnabled
                                                        ? 'Weight & images captured from weighing machine'
                                                        : cameraEnabled
                                                            ? 'Capture vehicle image (weight entered manually)'
                                                            : 'Capture weight from weighing machine'}
                                                </small>
                                            </div>
                                        </>
                                    )}
                                </Card.Body>
                            </Card>
                        ) : (
                            /* Quantity & Image Panel for non-weight items (e.g., Cft) */
                            <Card className="mb-3 border-0 shadow-sm" style={{ borderRadius: '8px' }}>
                                <Card.Header className="d-flex align-items-center justify-content-between py-2 px-4" style={{ borderRadius: '8px 8px 0 0', backgroundColor: '#e8f4fc', minHeight: '42px' }}>
                                    <div className="d-flex align-items-center">
                                        <i className={`bi ${readOnly ? 'bi-box-seam' : cameraEnabled ? 'bi-camera' : 'bi-box-seam'} me-2`} style={{ color: '#4a90a4' }}></i>
                                        <span className="fw-semibold small" style={{ color: '#4a90a4' }}>
                                            {readOnly ? 'QUANTITY & IMAGE' : cameraEnabled ? 'VEHICLE IMAGE' : 'QUANTITY'}
                                        </span>
                                    </div>
                                </Card.Header>
                                <Card.Body className="p-3">
                                    {/* Image or camera-off message */}
                                    {cameraEnabled ? (
                                        <div className="mb-3">
                                            {/* Step 1 saved LOADED image — shown in Step 2 so operator can see what arrived */}
                                            {!readOnly && isStep2Mode && (() => {
                                                const stepOneImage = savedImages.find(img => img.imageType === 'LOADED_WEIGHT');
                                                if (!stepOneImage) return null;
                                                return (
                                                    <div className="mb-2 border rounded overflow-hidden" style={{ borderColor: '#dee2e6' }}>
                                                        <div className="bg-light px-2 py-1 d-flex justify-content-between align-items-center" style={{ fontSize: '0.7rem' }}>
                                                            <span className="text-muted"><i className="bi bi-image me-1"></i>Step 1 — Loaded</span>
                                                            <span className="badge bg-info bg-opacity-10 text-info" style={{ fontSize: '0.6rem' }}>LOADED</span>
                                                        </div>
                                                        <img
                                                            src={`${API_URL}/api/vehicle-capture/image/${stepOneImage.primaryId}`}
                                                            alt="Step 1 loaded vehicle"
                                                            style={{ width: '100%', height: 'auto', display: 'block' }}
                                                        />
                                                    </div>
                                                );
                                            })()}
                                            {capturedLoadedImage ? (
                                                <div className="border rounded overflow-hidden" style={{ borderColor: '#198754' }}>
                                                    {isStep2Mode && (
                                                        <div className="bg-light px-2 py-1 d-flex justify-content-between align-items-center" style={{ fontSize: '0.7rem' }}>
                                                            <span className="text-muted"><i className="bi bi-image me-1"></i>Step 2 — Empty</span>
                                                            <span className="badge bg-success bg-opacity-10 text-success" style={{ fontSize: '0.6rem' }}>EMPTY</span>
                                                        </div>
                                                    )}
                                                    <img
                                                        src={capturedLoadedImage.url}
                                                        alt="Captured vehicle"
                                                        style={{ width: '100%', height: 'auto', display: 'block' }}
                                                    />
                                                </div>
                                            ) : !readOnly ? (
                                                <div
                                                    className="border rounded d-flex flex-column align-items-center justify-content-center bg-light"
                                                    style={{ height: '120px', borderStyle: 'dashed', borderColor: '#adb5bd' }}
                                                >
                                                    <i className="bi bi-camera text-muted" style={{ fontSize: '2rem', opacity: 0.4 }}></i>
                                                    <span className="badge bg-light text-muted mt-1" style={{ fontSize: '0.7rem' }}>
                                                        {isStep2Mode ? 'Awaiting empty-weight capture' : 'Awaiting capture'}
                                                    </span>
                                                </div>
                                            ) : null}
                                        </div>
                                    ) : !readOnly && (
                                        <div className="text-center p-3 mb-3 rounded" style={{ backgroundColor: '#fff3cd', border: '1px dashed #ffc107' }}>
                                            <i className="bi bi-camera-video-off text-warning" style={{ fontSize: '1.5rem' }}></i>
                                            <div className="text-muted small mt-1">Camera capture is disabled</div>
                                            <div className="text-muted" style={{ fontSize: '0.7rem' }}>Vehicle images are not required for this purchase</div>
                                        </div>
                                    )}

                                    {/* Quantity Display */}
                                    {(() => {
                                        const invItem = (formik.values.itemsList || []).find(i => i.itemType === 'INVENTORY');
                                        const unitLabel = invItem?.unitSymbol || 'Units';
                                        return (
                                            <div className="p-2 rounded text-center" style={{
                                                backgroundColor: invItem?.quantity > 0 ? '#d1e7dd' : '#f8f9fa',
                                                border: invItem?.quantity > 0 ? '1px solid #badbcc' : '1px solid #dee2e6'
                                            }}>
                                                <small className="text-muted d-block" style={{ fontSize: '0.7rem' }}>
                                                    Quantity ({unitLabel})
                                                </small>
                                                <span className="fw-bold" style={{
                                                    fontSize: '1.4rem',
                                                    color: invItem?.quantity > 0 ? '#198754' : '#6c757d'
                                                }}>
                                                    {invItem?.quantity > 0 ? `${invItem.quantity} ${unitLabel}` : `-- ${unitLabel}`}
                                                </span>
                                            </div>
                                        );
                                    })()}

                                    {!readOnly && cameraEnabled && (
                                        <>
                                            <div className="d-grid mt-3">
                                                <Button
                                                    variant="light"
                                                    size="sm"
                                                    className="text-primary"
                                                    style={{
                                                        border: '1px solid #86b7fe',
                                                        backgroundColor: '#f8f9fa'
                                                    }}
                                                    onClick={handleCapture}
                                                    disabled={isCapturing}
                                                >
                                                    {isCapturing ? (
                                                        <>
                                                            <Spinner animation="border" size="sm" className="me-2" />
                                                            Capturing...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <i className="bi bi-camera me-2"></i>
                                                            Capture Image
                                                        </>
                                                    )}
                                                </Button>
                                            </div>
                                            <div className="text-center mt-2">
                                                <small className="text-muted" style={{ fontSize: '0.7rem' }}>
                                                    <i className="bi bi-info-circle me-1"></i>
                                                    Capture vehicle image & enter quantity above
                                                </small>
                                            </div>
                                        </>
                                    )}
                                </Card.Body>
                            </Card>
                        )}

                        {/* Captured Images Card - Only in View Mode */}
                        {readOnly && savedImages.length > 0 && (
                            <Card className="mb-3 border-0 shadow-sm" style={{ borderRadius: '8px' }}>
                                <Card.Header className="py-2 px-4" style={{ borderRadius: '8px 8px 0 0', backgroundColor: '#e8f4fc', minHeight: '42px' }}>
                                    <div className="d-flex align-items-center">
                                        <i className="bi bi-images me-2" style={{ color: '#4a90a4' }}></i>
                                        <span className="fw-semibold small" style={{ color: '#4a90a4' }}>CAPTURED IMAGES</span>
                                        <span className="badge bg-secondary ms-2">{savedImages.length}</span>
                                    </div>
                                </Card.Header>
                                <Card.Body className="p-3">
                                    <Row className="g-2 justify-content-center">
                                        {savedImages.map((img, index) => (
                                            <Col xs={savedImages.length === 1 ? 12 : 6} key={img.primaryId || index}>
                                                <div className="border rounded overflow-hidden position-relative">
                                                    <img
                                                        src={`${API_URL}/api/vehicle-capture/image/${img.primaryId}`}
                                                        alt={img.imageType || 'Captured'}
                                                        style={{
                                                            width: '100%',
                                                            height: 'auto',
                                                            display: 'block'
                                                        }}
                                                    />
                                                    <div
                                                        className="position-absolute bottom-0 start-0 end-0 p-1 text-center"
                                                        style={{
                                                            background: 'rgba(0,0,0,0.6)',
                                                            fontSize: '0.65rem'
                                                        }}
                                                    >
                                                        <span className="text-white">
                                                            {img.imageType === 'LOADED_WEIGHT' ? 'Loaded' : 'Empty'}
                                                        </span>
                                                    </div>
                                                </div>
                                            </Col>
                                        ))}
                                    </Row>
                                </Card.Body>
                            </Card>
                        )}

                        {/* Additional Details - Compact */}
                        <Card className="mb-3 border-0 shadow-sm" style={{ borderRadius: '8px' }}>
                            <Card.Header className="py-2 px-4" style={{ borderRadius: '8px 8px 0 0', backgroundColor: '#e8f4fc', minHeight: '42px' }}>
                                <div className="d-flex align-items-center">
                                    <i className="bi bi-card-checklist me-2" style={{ color: '#4a90a4' }}></i>
                                    <span className="fw-semibold small" style={{ color: '#4a90a4' }}>ADDITIONAL DETAILS</span>
                                </div>
                            </Card.Header>
                            <Card.Body className="p-3">
                                <Row className="g-2">
                                    <Col md={6}>
                                        {readOnly ? (
                                            <div className="mb-2">
                                                <label className="form-label text-muted small mb-0">Driver</label>
                                                <div className="fw-semibold small">{formik.values.driverName || '-'}</div>
                                            </div>
                                        ) : (
                                            <Form.Group>
                                                <AGCText
                                                    formik={formik}
                                                    name="Driver"
                                                    id="driverName"
                                                    value={formik.values.driverName}
                                                    toolTip="Driver name"
                                                    required={false}
                                                />
                                            </Form.Group>
                                        )}
                                    </Col>
                                    <Col md={6}>
                                        {readOnly ? (
                                            <div className="mb-2">
                                                <label className="form-label text-muted small mb-0">Batta</label>
                                                <div className="fw-semibold small">{formik.values.battaAmount || '-'}</div>
                                            </div>
                                        ) : (
                                            <Form.Group>
                                                <AGCText
                                                    formik={formik}
                                                    name="Batta"
                                                    id="battaAmount"
                                                    value={formik.values.battaAmount}
                                                    toolTip="Driver batta expense"
                                                    required={false}
                                                />
                                            </Form.Group>
                                        )}
                                    </Col>
                                    <Col md={12}>
                                        {readOnly ? (
                                            <div className="mb-2">
                                                <label className="form-label text-muted small mb-0">Inspection Notes</label>
                                                <div className="fw-semibold small">{formik.values.inspectionNotes || '-'}</div>
                                            </div>
                                        ) : (
                                            <Form.Group>
                                                <AGCTextArea
                                                    formik={formik}
                                                    name="Inspection Notes"
                                                    id="inspectionNotes"
                                                    value={formik.values.inspectionNotes}
                                                    toolTip="Quality inspection notes"
                                                    rows={2}
                                                    required={false}
                                                />
                                            </Form.Group>
                                        )}
                                    </Col>
                                    <Col md={12}>
                                        {readOnly ? (
                                            <div className="mb-2">
                                                <label className="form-label text-muted small mb-0">Remarks</label>
                                                <div className="fw-semibold small">{formik.values.remarks || '-'}</div>
                                            </div>
                                        ) : (
                                            <Form.Group>
                                                <AGCTextArea
                                                    formik={formik}
                                                    name="Remarks"
                                                    id="remarks"
                                                    value={formik.values.remarks}
                                                    toolTip="Additional notes"
                                                    rows={2}
                                                    required={false}
                                                />
                                            </Form.Group>
                                        )}
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>

                        {/* Action Buttons */}
                        <div className="d-grid gap-2">
                            {readOnly ? (
                                <>
                                    <Button
                                        variant="light"
                                        onClick={() => navigate(`/purchase-print/${id}`)}
                                        size="lg"
                                        className="text-success"
                                        style={{ border: '1px solid #198754', backgroundColor: '#f8f9fa', borderRadius: '8px' }}
                                    >
                                        <i className="bi bi-printer me-2"></i>
                                        View Print Ticket
                                    </Button>
                                    <Button
                                        variant="light"
                                        onClick={handleCancel}
                                        size="lg"
                                        className="text-primary"
                                        style={{ border: '1px solid #86b7fe', backgroundColor: '#f8f9fa', borderRadius: '8px' }}
                                    >
                                        <i className="bi bi-arrow-left me-2"></i>
                                        Back to Purchases List
                                    </Button>
                                </>
                            ) : (
                                <>
                                    <Button
                                        type="button"
                                        variant="light"
                                        disabled={isSaving}
                                        size="lg"
                                        className="text-primary"
                                        style={{ border: '1px solid #86b7fe', backgroundColor: '#f8f9fa', borderRadius: '8px' }}
                                        onClick={() => handleSubmitWithValidation(formik.values)}
                                    >
                                        {isSaving && <Spinner animation="border" size="sm" className="me-2" />}
                                        <i className="bi bi-check-lg me-2"></i>
                                        {id ? 'Update Purchase' : 'Create Purchase'}
                                    </Button>
                                    <Button
                                        variant="light"
                                        onClick={handleCancel}
                                        disabled={isSaving}
                                        className="text-secondary"
                                        style={{ border: '1px solid #ced4da', backgroundColor: '#f8f9fa', borderRadius: '8px' }}
                                    >
                                        Cancel
                                    </Button>
                                </>
                            )}
                        </div>
                    </Col>
                </Row>
            </div>

            {/* Validation Error Modal */}
            <Modal
                show={showValidationError}
                onHide={() => setShowValidationError(false)}
                centered
                backdrop="static"
            >
                <Modal.Header closeButton className="bg-danger text-white">
                    <Modal.Title>
                        <i className="bi bi-x-circle me-2"></i>
                        Required Fields Missing
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="text-muted mb-3">Please fix the following errors before saving:</p>
                    <ul className="mb-0">
                        {validationErrors.map((error, index) => (
                            <li key={index} className="text-danger mb-2">
                                <i className="bi bi-exclamation-circle me-2"></i>
                                {error}
                            </li>
                        ))}
                    </ul>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="light"
                        className="text-danger"
                        style={{ border: '1px solid #dc3545', backgroundColor: '#f8f9fa' }}
                        onClick={() => setShowValidationError(false)}
                    >
                        <i className="bi bi-arrow-left me-1"></i>
                        Go Back & Fix
                    </Button>
                </Modal.Footer>
            </Modal>
        </Form>

        {/* Weight Capture Processing Modal */}
        <WeighbridgeCaptureModal show={isCapturing} weight={weightTrack} image={imageTrack} />
        {groupSelectModal}
        </>
    );
}

// Bill Calculations Component
function BillCalculations({ items, formik, taxPlansLookup = {}, readOnly = false, roundOffMode = 'CEILING' }) {
    // Parse numbers safely
    const parseNumber = (value) => {
        if (value === null || value === undefined || value === '') return 0;
        const parsed = parseFloat(value);
        return isNaN(parsed) ? 0 : parsed;
    };

    // Format currency
    const formatCurrency = (amount) => {
        if (amount === null || amount === undefined || isNaN(amount)) return '₹0.00';
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 2
        }).format(amount);
    };

    // Get tax percentage from lookup
    const getTaxPercentage = (tax) => {
        let percentage = tax.percentage || tax.taxPercentage;
        if ((percentage === null || percentage === undefined) && tax.id && taxPlansLookup[tax.id]) {
            percentage = taxPlansLookup[tax.id].taxPercentage;
        }
        if ((percentage === null || percentage === undefined) && tax.name && taxPlansLookup[tax.name]) {
            percentage = taxPlansLookup[tax.name].taxPercentage;
        }
        return parseNumber(percentage);
    };

    // Get tax name from lookup
    const getTaxName = (tax) => {
        let name = tax.name;
        if (!name && tax.id && taxPlansLookup[tax.id]) {
            name = taxPlansLookup[tax.id].name;
        }
        return name || 'Tax';
    };

    // Calculate line items
    const lineItems = items.map(item => {
        const price = parseNumber(item.price);
        const quantity = parseNumber(item.quantity);
        const itemDiscount = parseNumber(item.discountAmount);
        const subtotal = price * quantity;
        const afterDiscount = subtotal - itemDiscount;

        let taxAmount = 0;
        const taxDetails = [];
        if (item.taxPlans && item.taxPlans.length > 0) {
            item.taxPlans.forEach(tax => {
                const taxPercent = getTaxPercentage(tax);
                const taxName = getTaxName(tax);
                const taxAmt = (afterDiscount * taxPercent) / 100;
                taxAmount += taxAmt;
                taxDetails.push({ name: taxName, percentage: taxPercent, amount: taxAmt });
            });
        }

        const lineTotal = afterDiscount + taxAmount;

        return {
            name: item.inventoryItemName || 'Item',
            itemType: item.itemType,
            price,
            quantity,
            unit: item.unitSymbol || '',
            subtotal,
            itemDiscount,
            afterDiscount,
            taxDetails,
            taxAmount,
            lineTotal
        };
    }).sort((a, b) => {
        if (a.itemType === 'INVENTORY' && b.itemType !== 'INVENTORY') return -1;
        if (a.itemType !== 'INVENTORY' && b.itemType === 'INVENTORY') return 1;
        return 0;
    });

    // Calculate totals
    const subtotal = lineItems.reduce((sum, item) => sum + item.subtotal, 0);
    const totalItemDiscounts = lineItems.reduce((sum, item) => sum + item.itemDiscount, 0);
    const totalTax = lineItems.reduce((sum, item) => sum + item.taxAmount, 0);
    const totalAfterTax = lineItems.reduce((sum, item) => sum + item.lineTotal, 0);

    // Auto-calculate round off based on ROUND_OFF_MODE setting
    const parsedRoundOff = computeRoundOffAmount(totalAfterTax, roundOffMode);
    const grandTotal = totalAfterTax + parsedRoundOff;

    // Aggregate taxes by tax plan name
    const taxBreakdown = {};
    lineItems.forEach(item => {
        item.taxDetails.forEach(tax => {
            const key = `${tax.name} (${tax.percentage}%)`;
            if (!taxBreakdown[key]) {
                taxBreakdown[key] = { name: tax.name, percentage: tax.percentage, amount: 0 };
            }
            taxBreakdown[key].amount += tax.amount;
        });
    });
    const taxBreakdownList = Object.values(taxBreakdown);

    // Sync auto-calculated round off to formik
    useEffect(() => {
        formik.setFieldValue('roundOffAmount', parsedRoundOff);
    }, [totalAfterTax, roundOffMode]); // eslint-disable-line react-hooks/exhaustive-deps

    if (items.length === 0) {
        return (
            <div className="text-center text-muted py-3">
                <i className="bi bi-receipt fs-1 d-block mb-2 opacity-50"></i>
                <p className="mb-0">No items added yet</p>
                <small>Add items to see calculations</small>
            </div>
        );
    }

    return (
        <div className="bill-summary">
            {/* Line Items Table */}
            <div className="table-responsive">
                <table className="table table-sm mb-0" style={{ borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                    <thead>
                        <tr style={{ borderBottom: '2px solid #dee2e6' }}>
                            <th className="py-2 text-muted fw-normal" style={{ fontSize: '0.8rem', width: '40%' }}>Item</th>
                            <th className="py-2 text-muted fw-normal text-end" style={{ fontSize: '0.8rem', width: '20%' }}>Rate</th>
                            <th className="py-2 text-muted fw-normal text-center" style={{ fontSize: '0.8rem', width: '10%' }}>Qty</th>
                            <th className="py-2 text-muted fw-normal text-end" style={{ fontSize: '0.8rem', width: '15%' }}>Tax</th>
                            <th className="py-2 text-muted fw-normal text-end" style={{ fontSize: '0.8rem', width: '15%' }}>Amount</th>
                        </tr>
                    </thead>
                    <tbody>
                        {lineItems.map((item, index) => (
                            <tr key={index} style={{ borderBottom: '1px dashed #e9ecef' }}>
                                <td className="py-2 align-middle" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    <span className="fw-semibold" title={item.name}>{item.name}</span>
                                    {item.itemDiscount > 0 && (
                                        <small className="text-danger d-block">-{formatCurrency(item.itemDiscount)} disc</small>
                                    )}
                                </td>
                                <td className="text-end align-middle py-2">{formatCurrency(item.price)}</td>
                                <td className="text-center align-middle py-2">{item.quantity}</td>
                                <td className="text-end align-middle py-2">
                                    {item.taxAmount > 0 ? (
                                        <span className="text-success">{formatCurrency(item.taxAmount)}</span>
                                    ) : (
                                        <span className="text-muted">-</span>
                                    )}
                                </td>
                                <td className="text-end fw-semibold align-middle py-2">{formatCurrency(item.lineTotal)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Bill Summary Section */}
            <div className="mt-4 pt-3" style={{ borderTop: '2px solid #dee2e6' }}>
                {/* Subtotal */}
                <div className="d-flex justify-content-between py-2">
                    <span className="text-muted">Subtotal:</span>
                    <span className="fw-semibold">{formatCurrency(subtotal)}</span>
                </div>

                {/* Item Discounts */}
                {totalItemDiscounts > 0 && (
                    <div className="d-flex justify-content-between py-2" style={{ borderTop: '1px dotted #e9ecef' }}>
                        <span className="text-danger">Item Discounts:</span>
                        <span className="text-danger">-{formatCurrency(totalItemDiscounts)}</span>
                    </div>
                )}

                {/* Tax Breakdown */}
                {taxBreakdownList.length > 0 && (
                    <div style={{ borderTop: '1px dotted #e9ecef' }}>
                        {taxBreakdownList.map((tax, index) => (
                            <div key={index} className="d-flex justify-content-between py-2">
                                <span className="text-warning">{tax.name} ({tax.percentage}%):</span>
                                <span className="text-warning">{formatCurrency(tax.amount)}</span>
                            </div>
                        ))}
                    </div>
                )}

                {/* Round Off (auto-calculated) */}
                <div className="d-flex justify-content-between align-items-center py-2" style={{ borderTop: '1px dotted #e9ecef' }}>
                    <span className={parsedRoundOff >= 0 ? 'text-success' : 'text-danger'}>Round Off:</span>
                    <div className="d-flex align-items-center">
                        <span className={`me-1 ${parsedRoundOff >= 0 ? 'text-success' : 'text-danger'}`}>
                            {parsedRoundOff >= 0 ? '+ ₹' : '- ₹'}
                        </span>
                        <span className={`fw-semibold ${parsedRoundOff >= 0 ? 'text-success' : 'text-danger'}`} style={{ width: '100px', textAlign: 'end' }}>
                            {Math.abs(parsedRoundOff).toFixed(2)}
                        </span>
                    </div>
                </div>

                {/* Grand Total */}
                <div
                    className="d-flex justify-content-between align-items-center py-3 mt-2"
                    style={{
                        borderTop: '2px double #333',
                        borderBottom: '2px double #333',
                        backgroundColor: '#f8f9fa'
                    }}
                >
                    <span className="fw-bold fs-5">Grand Total:</span>
                    <span className="fw-bold fs-4 text-success">{formatCurrency(grandTotal)}</span>
                </div>
            </div>
        </div>
    );
}
